import React, { useState } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
} from "lucide-react";
import Header from "../components/organisms/Header";
import { addBooking } from "../lib/apis/booking";

// Type definitions
interface DoctorData {
  doctor_id: string;
  availability_id: string;
}

interface BookingData {
  booking_mode: string;
  booked_date: string;
  booked_slot_start: string | null;
  booked_slot_end: string | null;
  booking_session_start_time: string;
  booking_session_end_time: string;
  is_confirmed: boolean;
  symptoms: string;
  comments: string;
  availability_id: string;
  hospital_id: string;
  patient_id: string;
  doctor_id: string;
  token_number: number;
  queue_type: string;
  status: number;
  added_by: string;
  booking_lang?: string;
}

interface ApiResponse {
  success: boolean;
  id: string;
}

interface TestResults {
  total: number;
  successful: number;
  failed: number;
  responseTimes: number[];
  errors: string[];
  executionTime: number;
  averageResponseTime: number;
  successRate: number;
}

interface LogEntry {
  timestamp: string;
  message: string;
  type: "info" | "success" | "error";
}

type SessionType = "morning" | "afternoon" | "evening";

const BulkTesting: React.FC = () => {
  const morningSlots = [
    "06:40 - 06:41",
    "06:41 - 06:42",
    "06:42 - 06:43",
    "06:43 - 06:44",
    "06:44 - 06:45",
    "06:45 - 06:46",
    "06:46 - 06:47",
    "06:47 - 06:48",
    "06:48 - 06:49",
    "06:49 - 06:50",
    "06:50 - 06:51",
    "06:51 - 06:52",
    "06:52 - 06:53",
    "06:53 - 06:54",
    "06:54 - 06:55",
    "06:55 - 06:56",
    "06:56 - 06:57",
    "06:57 - 06:58",
    "06:58 - 06:59",
    "06:59 - 07:00",
    "07:00 - 07:01",
    "07:01 - 07:02",
    "07:02 - 07:03",
    "07:03 - 07:04",
    "07:04 - 07:05",
    "07:05 - 07:06",
    "07:06 - 07:07",
    "07:07 - 07:08",
    "07:08 - 07:09",
    "07:09 - 07:10",
    "07:10 - 07:11",
    "07:11 - 07:12",
    "07:12 - 07:13",
    "07:13 - 07:14",
    "07:14 - 07:15",
    "07:15 - 07:16",
    "07:16 - 07:17",
    "07:17 - 07:18",
    "07:18 - 07:19",
    "07:19 - 07:20",
    "07:20 - 07:21",
    "07:21 - 07:22",
    "07:22 - 07:23",
    "07:23 - 07:24",
    "07:24 - 07:25",
    "07:25 - 07:26",
    "07:26 - 07:27",
    "07:27 - 07:28",
    "07:28 - 07:29",
    "07:29 - 07:30",
    "07:30 - 07:31",
    "07:31 - 07:32",
    "07:32 - 07:33",
    "07:33 - 07:34",
    "07:34 - 07:35",
    "07:35 - 07:36",
    "07:36 - 07:37",
    "07:37 - 07:38",
    "07:38 - 07:39",
    "07:39 - 07:40",
    "07:40 - 07:41",
    "07:41 - 07:42",
    "07:42 - 07:43",
    "07:43 - 07:44",
    "07:44 - 07:45",
    "07:45 - 07:46",
    "07:46 - 07:47",
    "07:47 - 07:48",
    "07:48 - 07:49",
    "07:49 - 07:50",
    "07:50 - 07:51",
    "07:51 - 07:52",
    "07:52 - 07:53",
    "07:53 - 07:54",
    "07:54 - 07:55",
    "07:55 - 07:56",
    "07:56 - 07:57",
    "07:57 - 07:58",
    "07:58 - 07:59",
    "07:59 - 08:00",
    "08:00 - 08:01",
    "08:01 - 08:02",
    "08:02 - 08:03",
    "08:03 - 08:04",
    "08:04 - 08:05",
    "08:05 - 08:06",
    "08:06 - 08:07",
    "08:07 - 08:08",
    "08:08 - 08:09",
    "08:09 - 08:10",
    "08:10 - 08:11",
    "08:11 - 08:12",
    "08:12 - 08:13",
    "08:13 - 08:14",
    "08:14 - 08:15",
    "08:15 - 08:16",
    "08:16 - 08:17",
    "08:17 - 08:18",
    "08:18 - 08:19",
    "08:19 - 08:20",
    "08:20 - 08:21",
    "08:21 - 08:22",
    "08:22 - 08:23",
    "08:23 - 08:24",
    "08:24 - 08:25",
    "08:25 - 08:26",
    "08:26 - 08:27",
    "08:27 - 08:28",
    "08:28 - 08:29",
    "08:29 - 08:30",
    "08:30 - 08:31",
    "08:31 - 08:32",
    "08:32 - 08:33",
    "08:33 - 08:34",
    "08:34 - 08:35",
    "08:35 - 08:36",
    "08:36 - 08:37",
    "08:37 - 08:38",
    "08:38 - 08:39",
    "08:39 - 08:40",
    "08:40 - 08:41",
    "08:41 - 08:42",
    "08:42 - 08:43",
    "08:43 - 08:44",
    "08:44 - 08:45",
    "08:45 - 08:46",
    "08:46 - 08:47",
    "08:47 - 08:48",
    "08:48 - 08:49",
    "08:49 - 08:50",
    "08:50 - 08:51",
    "08:51 - 08:52",
    "08:52 - 08:53",
    "08:53 - 08:54",
    "08:54 - 08:55",
    "08:55 - 08:56",
    "08:56 - 08:57",
    "08:57 - 08:58",
    "08:58 - 08:59",
    "08:59 - 09:00",
    "09:00 - 09:01",
    "09:01 - 09:02",
    "09:02 - 09:03",
    "09:03 - 09:04",
    "09:04 - 09:05",
    "09:05 - 09:06",
    "09:06 - 09:07",
    "09:07 - 09:08",
    "09:08 - 09:09",
    "09:09 - 09:10",
    "09:10 - 09:11",
    "09:11 - 09:12",
    "09:12 - 09:13",
    "09:13 - 09:14",
    "09:14 - 09:15",
    "09:15 - 09:16",
    "09:16 - 09:17",
    "09:17 - 09:18",
    "09:18 - 09:19",
    "09:19 - 09:20",
    "09:20 - 09:21",
    "09:21 - 09:22",
    "09:22 - 09:23",
    "09:23 - 09:24",
    "09:24 - 09:25",
    "09:25 - 09:26",
    "09:26 - 09:27",
    "09:27 - 09:28",
    "09:28 - 09:29",
    "09:29 - 09:30",
    "09:30 - 09:31",
    "09:31 - 09:32",
    "09:32 - 09:33",
    "09:33 - 09:34",
    "09:34 - 09:35",
    "09:35 - 09:36",
    "09:36 - 09:37",
    "09:37 - 09:38",
    "09:38 - 09:39",
    "09:39 - 09:40",
    "09:40 - 09:41",
    "09:41 - 09:42",
    "09:42 - 09:43",
    "09:43 - 09:44",
    "09:44 - 09:45",
    "09:45 - 09:46",
    "09:46 - 09:47",
    "09:47 - 09:48",
    "09:48 - 09:49",
    "09:49 - 09:50",
    "09:50 - 09:51",
    "09:51 - 09:52",
    "09:52 - 09:53",
    "09:53 - 09:54",
    "09:54 - 09:55",
    "09:55 - 09:56",
    "09:56 - 09:57",
    "09:57 - 09:58",
    "09:58 - 09:59",
    "09:59 - 10:00",
    "10:00 - 10:01",
    "10:01 - 10:02",
    "10:02 - 10:03",
    "10:03 - 10:04",
    "10:04 - 10:05",
    "10:05 - 10:06",
    "10:06 - 10:07",
    "10:07 - 10:08",
    "10:08 - 10:09",
    "10:09 - 10:10",
    "10:10 - 10:11",
    "10:11 - 10:12",
    "10:12 - 10:13",
    "10:13 - 10:14",
    "10:14 - 10:15",
    "10:15 - 10:16",
    "10:16 - 10:17",
    "10:17 - 10:18",
    "10:18 - 10:19",
    "10:19 - 10:20",
    "10:20 - 10:21",
    "10:21 - 10:22",
    "10:22 - 10:23",
    "10:23 - 10:24",
    "10:24 - 10:25",
    "10:25 - 10:26",
    "10:26 - 10:27",
    "10:27 - 10:28",
    "10:28 - 10:29",
    "10:29 - 10:30",
    "10:30 - 10:31",
    "10:31 - 10:32",
    "10:32 - 10:33",
    "10:33 - 10:34",
    "10:34 - 10:35",
    "10:35 - 10:36",
    "10:36 - 10:37",
    "10:37 - 10:38",
    "10:38 - 10:39",
    "10:39 - 10:40",
    "10:40 - 10:41",
    "10:41 - 10:42",
    "10:42 - 10:43",
    "10:43 - 10:44",
    "10:44 - 10:45",
    "10:45 - 10:46",
    "10:46 - 10:47",
    "10:47 - 10:48",
    "10:48 - 10:49",
    "10:49 - 10:50",
    "10:50 - 10:51",
    "10:51 - 10:52",
    "10:52 - 10:53",
    "10:53 - 10:54",
    "10:54 - 10:55",
    "10:55 - 10:56",
    "10:56 - 10:57",
    "10:57 - 10:58",
    "10:58 - 10:59",
    "10:59 - 11:00",
    "11:00 - 11:01",
    "11:01 - 11:02",
    "11:02 - 11:03",
    "11:03 - 11:04",
    "11:04 - 11:05",
    "11:05 - 11:06",
    "11:06 - 11:07",
    "11:07 - 11:08",
    "11:08 - 11:09",
    "11:09 - 11:10",
    "11:10 - 11:11",
    "11:11 - 11:12",
    "11:12 - 11:13",
    "11:13 - 11:14",
    "11:14 - 11:15",
    "11:15 - 11:16",
    "11:16 - 11:17",
    "11:17 - 11:18",
    "11:18 - 11:19",
    "11:19 - 11:20",
    "11:20 - 11:21",
    "11:21 - 11:22",
    "11:22 - 11:23",
    "11:23 - 11:24",
    "11:24 - 11:25",
    "11:25 - 11:26",
    "11:26 - 11:27",
    "11:27 - 11:28",
    "11:28 - 11:29",
    "11:29 - 11:30",
  ];

  const afternoonTokens: number[] = Array.from(
    { length: 270 },
    (_, i) => i + 1
  );

  const morningDoctorData: DoctorData[] = [
    {
      doctor_id: "acf3be8f-0e8d-4d88-bd93-51eea4023460",
      availability_id: "ae7aa585-7f3f-4373-8e71-e2ff3bf98a5c",
    },
    {
      doctor_id: "5ad99e13-176d-44e9-a2a1-c8f3464bf2ed",
      availability_id: "a11a813b-85e8-41b2-b8c6-c113cb29cc27",
    },
    {
      doctor_id: "3a1c3fc8-1152-40b2-b6f9-8dc8f693326b",
      availability_id: "bf6a3a26-c881-4d9e-a649-d0ba0badb9b7",
    },
  ];

  const afternoonDoctorData: DoctorData[] = [
    {
      doctor_id: "acf3be8f-0e8d-4d88-bd93-51eea4023460",
      availability_id: "25fc698e-a03a-46b5-aa94-714b9ebdea23",
    },
    {
      doctor_id: "5ad99e13-176d-44e9-a2a1-c8f3464bf2ed",
      availability_id: "03d8cef2-320f-4339-afab-a4e6c939dbd5",
    },
    {
      doctor_id: "3a1c3fc8-1152-40b2-b6f9-8dc8f693326b",
      availability_id: "5a7003c5-d6d7-4473-be96-f4a4560bc36a",
    },
  ];

  const eveningDoctorData: DoctorData[] = [
    {
      doctor_id: "acf3be8f-0e8d-4d88-bd93-51eea4023460",
      availability_id: "ae7aa585-7f3f-4373-8e71-e2ff3bf98a5c",
    },
    {
      doctor_id: "5ad99e13-176d-44e9-a2a1-c8f3464bf2ed",
      availability_id: "9784e018-00aa-4afb-854d-a05da85cfde0",
    },
    {
      doctor_id: "3a1c3fc8-1152-40b2-b6f9-8dc8f693326b",
      availability_id: "dbcf960f-b058-4297-8761-b841524d4a38",
    },
  ];

  const patientIds: string[] = [
    "1f045200-7e6a-4404-af18-4c57b4987e68",
    "200e8fc8-ba87-421c-af26-408aa1b569db",
    "bb01011b-eba4-40c5-bfdf-2967be2ec08d",
  ];

  // State management
  const [bookings, setBookings] = useState<number>(10);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [currentBooking, setCurrentBooking] = useState<number>(0);
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  // Helper functions to generate booking data
  const getMorningBookingData = (
    booked_slot_start: string,
    booked_slot_end: string,
    availability_id: string,
    patient_id: string,
    doctor_id: string
  ): BookingData => ({
    booking_mode: "booking",
    booked_date: "2025-08-24",
    booked_slot_start,
    booked_slot_end,
    booking_session_start_time: "06:30:00",
    booking_session_end_time: "11:30:00",
    is_confirmed: true,
    symptoms: "test",
    comments: "",
    availability_id,
    hospital_id: "7e32dbc9-4404-4688-b01b-eaa06ef456eb",
    patient_id,
    doctor_id,
    token_number: 0,
    queue_type: "Slot_Token",
    status: 0,
    added_by: "22acc2d9-a4af-4430-ab1c-c79909b8de11",
  });

  const getAfternoonBookingData = (
    availability_id: string,
    patient_id: string,
    doctor_id: string,
    token_number: number
  ): BookingData => ({
    booking_mode: "booking",
    booked_date: "2025-08-24",
    booked_slot_start: null,
    booked_slot_end: null,
    booking_session_start_time: "13:00:00",
    booking_session_end_time: "17:30:00",
    is_confirmed: true,
    symptoms: "test",
    comments: "",
    availability_id,
    hospital_id: "7e32dbc9-4404-4688-b01b-eaa06ef456eb",
    patient_id,
    doctor_id,
    token_number,
    queue_type: "Token",
    status: 0,
    added_by: "22acc2d9-a4af-4430-ab1c-c79909b8de11",
  });

  const getEveningBookingData = (
    availability_id: string,
    patient_id: string,
    doctor_id: string
  ): BookingData => ({
    booking_mode: "booking",
    booked_date: "2025-08-24",
    booked_slot_start: null,
    booked_slot_end: null,
    booking_session_start_time: "18:30:00",
    booking_session_end_time: "23:00:00",
    is_confirmed: true,
    symptoms: "test",
    comments: "",
    availability_id,
    hospital_id: "7e32dbc9-4404-4688-b01b-eaa06ef456eb",
    patient_id,
    doctor_id,
    token_number: 0,
    queue_type: "Session",
    status: 0,
    added_by: "22acc2d9-a4af-4430-ab1c-c79909b8de11",
    booking_lang: "en",
  });

  const addLog = (message: string, type: LogEntry["type"] = "info"): void => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, message, type }]);
  };

  const handleBegin = async (): Promise<void> => {
    if (bookings <= 0) {
      addLog("Please enter a valid number of bookings", "error");
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setCurrentBooking(0);
    setTestResults(null);
    setLogs([]);

    const startTime = Date.now();
    const results: Omit<
      TestResults,
      "executionTime" | "averageResponseTime" | "successRate"
    > = {
      total: bookings,
      successful: 0,
      failed: 0,
      responseTimes: [],
      errors: [],
    };

    addLog(`Starting bulk test with ${bookings} bookings...`, "info");

    try {
      for (let index = 0; index < bookings; index++) {

        setCurrentBooking(index + 1);
        setProgress(((index + 1) / bookings) * 100);

        const requestStartTime = Date.now();

        try {
          const sessions: SessionType[] = ["morning", "afternoon", "evening"];
          const session: SessionType = sessions[Math.floor(Math.random() * 3)];
          const patientIndex = Math.floor(Math.random() * patientIds.length);

          let req: BookingData;

          if (session === "morning") {
            const morningIndex = Math.floor(
              Math.random() * morningDoctorData.length
            );
            const morningSlotIndex = Math.floor(
              Math.random() * morningSlots.length
            );
            const [start, end] = morningSlots[morningSlotIndex].split(" - ");

            req = getMorningBookingData(
              start + ":00",
              end + ":00",
              morningDoctorData[morningIndex].availability_id,
              patientIds[patientIndex],
              morningDoctorData[morningIndex].doctor_id
            );
          } else if (session === "afternoon") {
            const afternoonIndex = Math.floor(
              Math.random() * afternoonDoctorData.length
            );
            const afternoonTokenIndex = Math.floor(
              Math.random() * afternoonTokens.length
            );

            req = getAfternoonBookingData(
              afternoonDoctorData[afternoonIndex].availability_id,
              patientIds[patientIndex],
              afternoonDoctorData[afternoonIndex].doctor_id,
              afternoonTokens[afternoonTokenIndex]
            );
          } else {
            const eveningIndex = Math.floor(
              Math.random() * eveningDoctorData.length
            );

            req = getEveningBookingData(
              eveningDoctorData[eveningIndex].availability_id,
              patientIds[patientIndex],
              eveningDoctorData[eveningIndex].doctor_id
            );
          }

          const res = await addBooking(req);
          console.log(res);
          
          const responseTime = Date.now() - requestStartTime;
          results.successful++;
          results.responseTimes.push(responseTime);

          if (index % 10 === 0 || index === bookings - 1) {
            addLog(
              `Booking ${
                index + 1
              }/${bookings} completed successfully (${session})`,
              "success"
            );
          }
        } catch (error) {
          const responseTime = Date.now() - requestStartTime;
          results.failed++;
          results.responseTimes.push(responseTime);
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          results.errors.push(errorMessage);
          addLog(`Booking ${index + 1} failed: ${errorMessage}`, "error");
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addLog(`Test execution failed: ${errorMessage}`, "error");
    }

    const endTime = Date.now();
    const totalExecutionTime = endTime - startTime;
    const averageResponseTime =
      results.responseTimes.length > 0
        ? results.responseTimes.reduce((a, b) => a + b, 0) /
          results.responseTimes.length
        : 0;
    const successRate = (results.successful / results.total) * 100;

    setTestResults({
      ...results,
      executionTime: totalExecutionTime,
      averageResponseTime,
      successRate,
    });

    addLog(`Test completed! Success rate: ${successRate.toFixed(2)}%`, "info");
    setIsRunning(false);
  };

  const handleReset = (): void => {
    setIsRunning(false);
    setProgress(0);
    setCurrentBooking(0);
    setTestResults(null);
    setLogs([]);
    addLog("Test reset", "info");
  };

  const formatTime = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const handleBookingsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setBookings(Number(e.target.value));
  };

  return (
    <div className="w-full">
      <Header header value="Bulk Testing" />
      <div className="mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FileText className="w-8 h-8" />
              Bulk Testing Dashboard
            </h1>
            <p className="text-blue-100 mt-2">
              Load test your booking API with comprehensive reporting
            </p>
          </div>

          <div className="p-8">
            {/* Controls */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Bookings
                  </label>
                  <input
                    type="number"
                    value={bookings}
                    onChange={handleBookingsChange}
                    disabled={isRunning}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    min="1"
                    max="1000"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBegin}
                    disabled={isRunning || bookings <= 0}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg font-medium hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  >
                    {isRunning ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    {isRunning ? "Running..." : "Start Test"}
                  </button>

                  <button
                    onClick={handleReset}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2 transition-all"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                </div>
              </div>

              {/* Progress */}
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>
                      {currentBooking}/{bookings}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {progress.toFixed(1)}%
                  </div>
                </div>

                {isRunning && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm">Test in progress...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            {testResults && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  Test Results
                </h2>

                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Successful
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                      {testResults.successful}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium text-red-800">
                        Failed
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-red-700">
                      {testResults.failed}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">
                        Avg Response
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                      {formatTime(testResults.averageResponseTime)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">
                        Success Rate
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-purple-700">
                      {testResults.successRate.toFixed(1)}%
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Summary</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p>
                        <span className="font-medium">
                          Total Execution Time:
                        </span>{" "}
                        {formatTime(testResults.executionTime)}
                      </p>
                      <p>
                        <span className="font-medium">Total Requests:</span>{" "}
                        {testResults.total}
                      </p>
                    </div>
                    <div>
                      <p>
                        <span className="font-medium">
                          Requests per Second:
                        </span>{" "}
                        {(
                          (testResults.total / testResults.executionTime) *
                          1000
                        ).toFixed(2)}
                      </p>
                      <p>
                        <span className="font-medium">Error Rate:</span>{" "}
                        {(
                          (testResults.failed / testResults.total) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Logs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Execution Log
              </h3>
              <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                {logs.length === 0 ? (
                  <p className="text-gray-400 text-sm">
                    No logs yet. Start a test to see execution details.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {logs.map((log, index) => (
                      <div key={index} className="text-sm font-mono flex gap-3">
                        <span className="text-gray-400">{log.timestamp}</span>
                        <span
                          className={
                            log.type === "error"
                              ? "text-red-400"
                              : log.type === "success"
                              ? "text-green-400"
                              : "text-gray-300"
                          }
                        >
                          {log.message}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkTesting;
